import React from "react";
import styled, { useTheme } from "styled-components";
import {
  LanguageNameSpaces,
  LineAnnotation,
  PointAnnotation,
  TimeRange,
} from "@shared/types";
import useTradingViewChart from "common/Charts/useTradingViewChart";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { SpinSize } from "antd/es/spin";
import { EmptyStatesType } from "common/Icons/types.ts";
import EmptyView from "common/Empty/EmptyView.tsx";
import { TRADING_VIEW_TIME_RANGES } from "@shared/common/constants.ts";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";

interface TradingViewChartProps {
  symbol?: string;
  exchange?: string;
  annotations?: (LineAnnotation | PointAnnotation)[];
  isCard?: boolean;
  timeRanges?: string[];
  minimize?: boolean;
  showExtendButton?: boolean;
  defaultTimeRange?: TimeRange;
  startTimestamp?: number;
  endTimestamp?: number;
  shouldFitToOrders?: boolean;
  spinnerSize?: SpinSize;
  object?: string;
}

const TradingViewChart = ({
  symbol,
  exchange,
  annotations = DEFAULT_ANNOTATIONS,
  isCard,
  timeRanges = TRADING_VIEW_TIME_RANGES,
  minimize,
  showExtendButton,
  defaultTimeRange,
  startTimestamp,
  endTimestamp,
  shouldFitToOrders,
  spinnerSize = "large",
  object,
}: TradingViewChartProps) => {
  const theme = useTheme();
  const { t } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { isLoading, chartContainerRef, isValidInExchange } =
    useTradingViewChart(
      symbol,
      exchange,
      timeRanges,
      annotations,
      isCard,
      minimize,
      showExtendButton,
      defaultTimeRange,
      startTimestamp,
      endTimestamp,
      shouldFitToOrders,
      object
    );

  return (
    <LoadingSpinnerOverlay
      isLoading={isLoading || !exchange || !symbol}
      hideContentWhileLoading={true}
      wrap={true}
      wrapStyle={{ height: "100%" }}
      spinnerSize={spinnerSize}
      backgroundColor={
        theme.colors[isCard || theme.isNewLook ? "cardBgColor" : "layoutBgColor"]
      }
      hideWithOpacity={true}
    >
      <Chart ref={chartContainerRef} $hide={!isValidInExchange} />
      {!isValidInExchange && (
        <EmptyView
          iconKey={EmptyStatesType.Graph}
          iconWidth={120}
          title={t("empty.no_data")}
        />
      )}
    </LoadingSpinnerOverlay>
  );
};

export default React.memo(TradingViewChart);

const DEFAULT_ANNOTATIONS: (LineAnnotation | PointAnnotation)[] = [];

const Chart = styled.div<{ $hide?: boolean }>`
  height: 100%;
  ${(p) => p.$hide && `display: none;`}
`;
